import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useOrganizationService } from '../services';

/**
 * Hook for managing integration status.
 * Fetches active integrations and provides a function to check if an integration is active.
 * 
 * @returns {Object} Object containing isIntegrationActive function to check if an integration is active
 */
export const useIntegrationStatus = () => {
  const { fetchOrgIntegrations } = useOrganizationService();
  const { activeIntegrations = [] } = useSelector((state: any) => state.organization);

  // Fetch active integrations on mount
  useEffect(() => {
    fetchOrgIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Intentionally excluding fetchOrgIntegrations to prevent infinite loops

  /**
   * Check if an integration is active based on its type
   * 
   * @param {string} integrationType - The type of integration to check (e.g., 'azure', 'jira')
   * @returns {boolean} True if the integration is active, false otherwise
   */
  const isIntegrationActive = (integrationType: string): boolean => {
    if (!integrationType) return false;
    
    return activeIntegrations.some(
      (integration: any) => 
        integration.integration_type === integrationType.toLowerCase() && 
        integration.is_active
    );
  };

  /**
   * Check if integration data should be shown based on the integration_data object
   * 
   * @param {Object} integrationData - The integration data object
   * @returns {boolean} True if integration data should be shown, false otherwise
   */
  const shouldShowIntegration = (integrationData: any): boolean => {
    if (!integrationData || !integrationData.integration_type) return false;
    
    return isIntegrationActive(integrationData.integration_type);
  };

  return {
    activeIntegrations,
    isIntegrationActive,
    shouldShowIntegration
  };
};

export default useIntegrationStatus; 